import { HttpClient } from "@angular/common/http";
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, NavigationExtras, Router } from "@angular/router";
import {
  MenuController,
  ModalController,
  ToastController,
} from "@ionic/angular";
import { CookieService } from 'ngx-cookie-service';
import { of, Subject } from "rxjs";
import {
  distinctUntilChanged,
  filter,
  repeat,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap,
} from "rxjs/operators";
import { ConfirmModalCartComponent } from 'src/app/components/confirm-modal-cart/confirm-modal-cart.component';
import { CouponFlows } from "src/app/flows/coupon.flows";
import {
  Order,
} from 'src/app/models/models';
import { MRWPoint } from 'src/app/models/mrw-point.model';
import { Finishing, Settings, ShippingType } from 'src/app/shared-lib/core/settings';
import { calculateCouponEligibleAmount, Coupon, CouponService } from 'src/app/shared-lib/domains/coupon';
import { OrderShippingStatus } from 'src/app/shared-lib/domains/order';
import { CheckoutResponse, OrderPaymentStatus, PaymentType } from 'src/app/shared-lib/domains/payment';
import { PickupPointService } from 'src/app/shared-lib/domains/pickup-point/pickup-point.service';
import { ProductType } from 'src/app/shared-lib/domains/product';
import { TransactionService } from 'src/app/shared-lib/domains/transaction';
import { UserService } from "src/app/shared-lib/domains/user";
import { WalletService } from 'src/app/shared-lib/domains/wallet';
import { DiscountType, ProvinceModel } from 'src/app/shared-lib/global/models';
import { AppService } from 'src/app/shared/app.service';
import { CartService } from 'src/app/shared/cart.service';
import { OrderStateService } from 'src/app/shared/order-state.service';
import { OrderService } from 'src/app/shared/order.service';
import { ShippingService } from 'src/app/shared/services/shipping.service';
import { environment } from 'src/environments/environment';
import { OrderProcessStatus } from '../../shared-lib/domains/order';

export const ShippingHintTextOptions = {
  [ShippingType.ECONOMIC]:
    "Recibe en 1-3 días si pides antes de las 2 de la tarde entre Lunes y Jueves.",
  [ShippingType.STANDARD]:
    "Recibe en 1 o 2 días hábiles si pides antes de las 2 de la tarde entre Lunes y Jueves.",
  [ShippingType.STANDARD_24]:
    "Recibe al día siguiente si pides antes de las 2 de la tarde entre Lunes y Jueves.",
  [ShippingType.URGENT]:
    "Recibe a la mañana siguiente si pides antes de las 2 de la tarde entre Lunes y Jueves.",
  [ShippingType.PICKUP]: "",
};

@Component({
  selector: 'app-cart-preview',
  templateUrl: './cart-preview.component.html',
  styleUrls: ['./cart-preview.component.scss'],
  standalone: false
})
export class CartPreviewComponent implements OnInit, OnDestroy {
  shippingTypeLabels = {
    [ShippingType.ECONOMIC]: "Envío económico",
    [ShippingType.STANDARD]: "Envío estándar",
    [ShippingType.STANDARD_24]: "Envío prioritario",
    [ShippingType.URGENT]: "Envío urgente",
  };

  @Input() payingProcess: boolean = false;
  @Input() disabled: boolean;
  @Input() checkoutOutput: CheckoutResponse;

  productType = ProductType;
  discountType = DiscountType;
  freeShipping: boolean = false;
  _paymentType = PaymentType;
  shippingAddress: UntypedFormGroup;
  billingAddress: UntypedFormGroup;
  shippingType = ShippingType;

  isLoading: boolean = false;
  order = null;
  orderFormGroup: UntypedFormGroup;
  private componetDestroyed = new Subject();
  pointSelected: MRWPoint = null;

  shippingHintText = "";
  shippintFreeLimit = 0;
  allProvincesStrings;
  destroyRef = inject(DestroyRef);

  shippingCosts: number;
  storeProductsInCart: any[] = [];

  constructor(
    private menuCtrl: MenuController,
    public cartService: CartService,
    private router: Router,
    private orderStateService: OrderStateService,
    protected appS: AppService,
    public userService: UserService,
    private pickupService: PickupPointService,
    public shippingService: ShippingService,
    private route: ActivatedRoute,
    private orderService: OrderService,
    public toastController: ToastController,
    private http: HttpClient,
    private couponFlows: CouponFlows,
    private couponService: CouponService,
    private fb: UntypedFormBuilder,
    private modalController: ModalController,
    public walletService: WalletService,
    public transactionService: TransactionService,
    private cookieService: CookieService,
    private cdr: ChangeDetectorRef
  ) {
    this.orderFormGroup = this.newOrderForm();
  }

  ngOnInit() {

    this.cartService.normalizeFiles();

    // suscripción a Orden
    this.orderStateService.order$
      .pipe(takeUntil(this.componetDestroyed))
      .subscribe((order) => {
        this.order = order;
        this.getShippingCosts();
      });
    this.cartService
      .onCartRefreshed()
      .pipe(takeUntil(this.componetDestroyed))
      .subscribe((order) => {
        this.getShippingCosts();
      });

    this.cartService.paymentResult$
      .pipe(
        distinctUntilChanged(),
        tap((id) => {
          this.handlePaymentResultFlow();
        }),
        repeat()
      )
      .subscribe();

    this.route.queryParams.subscribe(async (params) => {
      this.orderStateService.subscribeUser(false);
      const orderId = params.orderId;

      this.orderStateService.subscribeUser(true);
      this.cartService.refreshCartPrice();
    });

    this.menuCtrl.get("cart-preview").then((menu) => {
      menu.addEventListener("ionDidOpen", () => {
        this.cartService.registerPopover.next();
        // Hacer scroll al item con falta de stock
        const outOfStockIcon = document.querySelector(".out-of-stock-icon");
        outOfStockIcon?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center",
        });
        // Abrir popovers de falta de stock para cada carpeta
        setTimeout(() => {
          this.cartService.openPopoversWithStockExceeded();
        }, 500);
      });
    });
  }

  ngOnDestroy() {

  }

  private handlePaymentResultFlow() {
    if (!this.checkoutOutput?.transactionId) {
      return;
    }

    this.transactionService.getTransaction$(this.checkoutOutput.transactionId).pipe(
      filter(transaction => !!transaction?.orderId),
      tap(() => {

        this.cartService.clonnedOrder = null;
      }),
      switchMap(transaction =>
        this.orderService.get$(transaction.orderId).pipe(
          take(1),
          tap(order => {

            this.cartService.clonnedOrder = order;

            this.cartService.setClonnedDiscountWallet();
            this.getShippingCosts();
          }),
          switchMap(order => {
            let couponDiscount = this.getOrderCoupon();
            return couponDiscount ?
              this.couponService.getCouponByCode$(couponDiscount?.refId).pipe(
                take(1),
                tap(coupon => {
                  this.cartService.coupon = coupon;
                  this.cartService.canUseCoupon = true;
                })
              )
              : of(null);
          })
        )
      ),
      takeUntil(this.cartService.destroyCartPreview$)

    ).subscribe({

    });

    this.cartService.paymentResult$
      .pipe(
        switchMap(() =>
          this.transactionService.getTransaction$(
            this.checkoutOutput?.transactionId
          )
        ),
        takeUntil(this.cartService.destroyCartPreview$)

      )
      .subscribe({

      });
  }

  closePreview() {
    this.menuCtrl.close("cart-preview");
    if (
      this.cartService.currentStep === 1 &&
      !this.cartService.cart?.items.length
    ) {
      this.router.navigate(["/"]);
    }
  }

  goToCart() {
    this.cartService.goToCart();
    this.closePreview();
  }

  getProgressBarValue() {
    const basePrice =
      this.getBasePrice() + this.cartService.getDiscount(DiscountType.COUPON);

    //  El precio para el ENVÍO GRATUITO depende del código postal,
    return (
      basePrice /
      this.cartService.getFreeShippingAmount(
        this.cartService.order?.shippingAddress?.postalCode
      )
    );
  }

  getBasePrice(order?: Order) {
    return (
      (order ?? this.cartService).cart.price.totalAmt -
      this.cartService.getWalletDiscount() -
      (order ?? this.cartService).cart.shipping.price.amt -
      (this.cartService.coupon
        ? this.cartService.getDiscount(this.discountType.COUPON)
        : 0)
    );
  }

  getRemainingAmount() {
    return (
      this.cartService.getFreeShippingAmount(
        this.cartService.order?.shippingAddress?.postalCode
      ) -
      this.cartService.shippingProductPrice -
      this.cartService.getDiscount(DiscountType.COUPON)
    ).toFixed(2);
  }

  // TODO cambiar la forma de filtrar los printgroup, mejor con pipe o suscripcion
  printGroupItems(items) {
    return items.filter(
      (item) => item.product.type === ProductType.PRINT && !item.product.vendor
    );
  }

  getFolderName(item, i) {
    return item.product.folderName ?? `Grupo #${i + 1}`;
  }

  removePrintProduct(item) {
    const it = this.cartService.cart.items.findIndex((_item) => item === _item);
    this.cartService.cart.items.splice(it, 1);
    this.cartService.refreshCartPrice();
  }

  editPrintProduct(item) {
    const navigationExtras: NavigationExtras = { state: { item } };
    this.router.navigate(["/"], navigationExtras);
  }

  exceededBindingItems(printingGroup) {
    const elementsExceeded = [];
    if (printingGroup.printSettings[Settings.FINISHING] !== Finishing.BINDING) {
      return elementsExceeded;
    }
    const covers = printingGroup.binding.covers.filter((cov1) => {
      return this.cartService.bindingStockExceeded.covers.find(
        (cov2) => cov1.color === cov2.color && cov1.pageSize === cov2.pageSize
      );
    });

    const rings = printingGroup.binding.rings.filter((cov1) => {
      return this.cartService.bindingStockExceeded.rings.find(
        (cov2) => cov1.color === cov2.color && cov1.diameter == cov2.diameter
      );
    });

    covers.forEach((cov) => {
      elementsExceeded.push(`Tapa ${cov.name.toLowerCase()}`);
    });

    rings.forEach((ring) => {
      elementsExceeded.push(`Anilla ${ring.name.toLowerCase()}`);
    });

    return elementsExceeded;
  }

  getPrintProductDescription(item) {
    const sides = item.product.printingGroup.printSides;
    let ret = "";
    ret += `${sides} ${sides == 1 ? " página impresa" : " páginas impresas"}`;
    const finishing = item.product.printingGroup.printSettings.finishing;
    if (finishing == Finishing.BINDING || finishing == Finishing.BINDINGX) {
      ret += ` y ${item.product.extra.units} `;
      ret += `${item.product.extra.units == 1 ? "encuadernación" : "encuadernaciones"
        }`;
    }
    ret += " por duplicado";
    return ret;
  }

  /*
   * Check  if exist the product
   */
  productExist(items) {

    let exist = false;

    return !!items?.find(
      (item) =>
        item.product.type === this.productType.STORE ||
        item.product.type === this.productType.WALLETBONUS ||
        item.product.type === this.productType.GENERIC ||
        item.product.type === this.productType.PRINT /*&& item.product.vendor*/
    );
  }

  existStationeryProducts(items) {
    return !!items?.find(
      (item) =>
        item.product.type === this.productType.STORE ||
        item.product.type === this.productType.WALLETBONUS ||
        item.product.type === this.productType.GENERIC
    );
  }

  async newPrintProduct() {
    this.closePreview();

    if (this.cartService.hasErrors()) {
      const toast = await this.toastController.create({
        message:
          "Algunos archivos tienen errores. Elimínalos antes de continuar.",
        color: "danger",
        position: "bottom",
        cssClass: "fitWidthToast",
        duration: 2000,
      });
      await toast.present();
      return;
    }
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "reload";
    this.cookieService.delete("bindingSelected");
    const navigationExtras: NavigationExtras = { state: { item: "new" } };
    this.router.navigate(["/"], navigationExtras);
  }

  async onSaveDraft() {
    if (!this.isLoading) {
      if (this.order.userId) {
        this.isLoading = true;
        this.orderStateService
          .saveDraft()
          .then(async (_) => {
            this.orderStateService.resetOrder();
            // TODO mostrar mensajito
            this.menuCtrl.close("cart-preview");
            this.newPrintProduct();
            const toast = await this.toastController.create({
              icon: "assets/icon/circle-check.svg",
              message: "Tu pedido se ha guardado para más tarde",
              position: "bottom",
              duration: 3000,
              cssClass: "fitWidthToast successToast",
            });
            toast.present();
          })
          .catch(async (err) => {
            const toast = await this.toastController.create({
              icon: "assets/icon/alert.svg",
              message: "Ha ocurrido un error, prueba de nuevo más tarde",
              position: "bottom",
              duration: 3000,
              cssClass: "fitWidthToast errorToast",
            });
            toast.present();
            console.log("Error", err);
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else {
        this.menuCtrl.close("cart-preview");
        const returnPath = "/cart";
        this.router.navigate(["/auth/login"], { queryParams: { returnPath } });
      }
    }
  }

  // Aplicamos el cupon al carrito
  applyCoupon(coupon) {

    this.cartService.coupon = coupon;
    this.cartService.refreshCartPrice();
  }

  getProvincesStrings() {
    this.http
      .get<any>("./assets/data/provincias.json")
      .toPromise()
      .then((data: ProvinceModel[]) => {
        this.allProvincesStrings = data;
      });
  }

  onChanges(): void {
    // TODO CHECK vambio de email porel mismo manejo de usuario
    // Debemos colocarlo en el escuchadore genérico
    // //observa cambios en Email
    this.orderFormGroup
      .get("email")
      .valueChanges.pipe(takeUntil(this.componetDestroyed))
      .subscribe((val) => {
        if (val && this.orderFormGroup.get("email").valid) {
          if (!this.orderFormGroup.get("userId").value) {
            this.validateCoupon(null);
            this.cartService.userCoupon = null;
          }
        }
      });

    // TODO CHECK
    // Dejamos por ahora, parece estar funcionando bien
    // Se observa si se debe aplicar el cupon de user
    this.cartService.applyUserCoupon$
      .pipe(shareReplay(1), takeUntil(this.componetDestroyed))
      .subscribe((coupon) => {

        setTimeout(() => {
          this.validateCoupon(this.cartService.userCoupon);
        }, 100);
      });
  }

  validateCoupon(coupon) {
    const code = coupon ? coupon.code : null;

    // Si hay variación en el cupon veriicamos si hay que aplicarlo
    if (coupon !== this.cartService.coupon) {
      if (this.cartService.canUseCoupon && code) {
        const email = this.orderFormGroup.controls.email.value;
        this.couponFlows
          .validateCoupon({
            code: code.toUpperCase(),
            email,
            cartAmount: calculateCouponEligibleAmount(
              this.cartService.cart.price.totalAmt,
              this.cartService.cart.shipping.price.amt
            ),
          })
          .then((resp: Coupon) => {

            if (!resp.valid) {
              resp = null;
            }
            this.applyCoupon(resp);
          });
      } else {
        this.applyCoupon(null);
      }
    }
  }

  async openConfirmModal() {
    const modal = await this.modalController.create({
      component: ConfirmModalCartComponent,
      cssClass: "auto-height coupon-warning",
      backdropDismiss: false,
      componentProps: {
        warningCoupon: true,
      },
    });
    modal.present();
  }

  getShippingLabel() {
    return this.shippingTypeLabels[this.order.shippingType];
  }

  newOrderForm() {
    this.billingAddress = this.addressForm();
    this.billingAddress.controls.recipient.clearValidators();
    this.billingAddress.controls.phone.clearValidators();
    this.billingAddress.updateValueAndValidity();

    this.shippingAddress = this.addressForm();
    return this.fb.group(
      {
        _version: 1,
        _appVersion: environment.version,
        id: null,
        userId: null,
        number: [""],
        formStepShipping: this.fb.group({
          name: [
            "",
            [Validators.required, Validators.pattern(/^.{2,}\ .{2,}/)],
          ],
          email: [
            "",
            [
              Validators.email,
              Validators.required,
              Validators.pattern("^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,10}$"),
            ],
          ],
          phone: [
            "",
            [
              Validators.required,
              Validators.minLength(9),
              Validators.maxLength(9),
              Validators.pattern(/^[0-9]*$/)
            ],
          ],
          shippingType: this.shippingService.checkShippingTypeAvailability(
            ShippingType.ECONOMIC,
            this.shippingAddress,
            this.cartService
          ).available
            ? ShippingType.ECONOMIC
            : ShippingType.STANDARD,
          pickupAddress: null,
          shippingAddress: this.shippingAddress,
          billingAddress: this.billingAddress,
          shippingCourierService: null,
        }),

        cart: [{}],
        paymentStatus: [OrderPaymentStatus.PENDING],
        processStatus: [OrderProcessStatus.PENDING],
        shippingStatus: [OrderShippingStatus.PENDING],
        paymentType: [
          { value: this._paymentType.CREDITCARD },
          {
            validators: [Validators.required],
            updateOn: 'change',
          }
        ],
        paymentService: null,
        policy: [false, Validators.requiredTrue],
        ads: [null],
        printProtected: [null],
        ocupation: [null, [Validators.required]],
        gender: [null, [Validators.required]],
      }
      //{ updateOn: "blur" } Las validaciones se realizan en cada cambio de valor quitando esta opción
    );
  }

  addressForm(_address?) {
    const postalVal = [
      Validators.required,
      Validators.minLength(5),
      Validators.maxLength(5),
      Validators.pattern(/^(?:0[1-9]|[1-4]\d|5[0-2])\d{3}$/),
    ];
    const address = this.fb.group({
      recipient: [
        "",
        [Validators.required, Validators.pattern(/^.{2,}\ .{2,}/)],
      ],
      addressLine1: ["", Validators.required],
      addressLine2: [""],
      city: ["", Validators.required],
      province: ["", Validators.required],
      provinceId: [""],
      postalCode: [
        "",
        { validators: Validators.compose(postalVal), updateOn: "change" },
      ],
      country: [""],
      phone: [
        "",
        [
          Validators.required,
          Validators.minLength(9),
          Validators.maxLength(9),
          Validators.pattern(/^[0-9]*$/)
        ],
      ],
      organization: [""],
      pickupPointName: [""],
      pickupPointCode: [""],
      shippingCourier: [""],
      taxId: [""],
      dni: ["", { updateOn: "change" }],
    });
    return address;
  }

  keepMinZero(value: number) {
    return Math.max(0, value);
  }

  getAfterPrice(order?: Order) {
    let afterPrice =
      this.getBasePrice(order) +
      this.cartService.getDiscount(this.discountType.COUPON);

    if (this.cartService.coupon && this.cartService.coupon?.freeShipping) {
      const { amt } = this.cartService.getShippingPrice(
        (order?.shippingAddress ?? this.cartService).postalCode,
        (order ?? this.cartService).cart,
        ShippingType.STANDARD
      );
      afterPrice += amt;
    }
    return afterPrice;
  }

  cartHasBonusWallet() {
    return this.cartService.cart.items.some(
      (item) => item.product.type === this.productType.WALLETBONUS
    );
  }

  isOnlyFreeShipping(order?: Order) {
    if (!this.cartService.coupon) {
      return false;
    }

    const shippingAmt = this.cartService.getShippingPrice(
      (order?.shippingAddress ?? this.cartService).postalCode,
      (order ?? this.cartService).cart,
      ShippingType.STANDARD
    ).amt;
    return (
      this.cartService.coupon?.freeShipping &&
      shippingAmt + this.cartService.getDiscount(this.discountType.COUPON) === 0
    );
  }

  getShippingCosts() {
    this.shippingCosts = !!this.cartService.cart.items.length
      ? this.cartService.cart.shipping.price.amt
      : this.cartService.clonnedOrder?.cart.shipping.price.amt;
  }

  getOrderCoupon() {
    return this.cartService.clonnedOrder?.cart.discounts.find(
      (disc) => disc.type === DiscountType.COUPON
    );
  }

  goToStationery() {
    this.menuCtrl.close("cart-preview");
    this.router.navigate(["/papeleria"]);
  }

  getTaxLabel() {
    let label = '(IVA incluido';
    label += this.cartService.mustReduceTax ? ' 0%' : '';
    label += ')';
    return label;
  }
}

